import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { switchMap, filter, takeUntil, tap, take } from 'rxjs/operators';
import { SystemService } from 'src/app/services/system.service';
import mqtt, { MqttClient } from 'mqtt/dist/mqtt';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import { NotificationService } from 'src/app/shared/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TimeSelectorDialogComponent } from './time-selector-dialog.component';
import { SelectionDialogComponent } from 'src/app/support/service-popup/selection-dialog/selection-dialog.component';

@Component({
  selector: 'app-service-popup',
  templateUrl: './service-popup.component.html',
  styleUrls: ['./service-popup.component.css']
})
export class ServicePopupComponent implements OnInit, OnDestroy {
  // Add your popup logic here

  companyId: string = '';
  siteId: string = '';
  systemId: string | null = null;
  private mqttClient: MqttClient | null = null;
  mqttClientId = Math.random().toString(16).substr(2, 8); // Unique client ID

  authSubscription: any;
  isTerminalAdmin: boolean | null = null;

  version: string = '';
  releaseDate: string = '';
  jsdVersion: string = '';
  statisticsUrl: string = '';

  systemNumber: string = '';

  systemOnline: boolean = false;
  systemStateUnknown: boolean = true;
  systemStatusMessage: string = 'Unknown';

  serverUsed: string = '';

  dateFrom = moment().subtract(2, 'days').tz("Europe/Oslo").unix();
  dateTo = moment().tz("Europe/Oslo").unix();

  machines: any[] = [];
  machines$: any;

  network$: Observable<any> | null = null;
  lastNetworkStatusUpdate: string = '';
  sim$: Observable<any> | null = null;
  modem$: Observable<any> | null = null;

  routeSubscription: any;

  alerts$: Observable<any[]> | null = null;

  private destroy$ = new Subject<void>();

  system$: Observable<any>;
  systemIP: string = '';

  constructor(
    private route: ActivatedRoute,
    private systemService: SystemService,
    private notificationService: NotificationService,
    private auth: AuthService,
    public dialog: MatDialog
  ) {
    this.system$ = this.route.params.pipe(
      tap(params => {
        this.systemId = params['systemId'] ?? null;
      }),
      filter(() => !!this.systemId),
      switchMap(() => this.systemService.getSystemDetail$(this.systemId!) as any),
      tap((system: any) => {
        if (!system) return;

        this.systemNumber = system.number;
        this.statisticsUrl = `https://main.friggkantine.no/service/grafana/d/ad4vjbn/system-status?orgId=1&from=now-24h&to=now&timezone=browser&kiosk=1&theme=light&refresh=5s&var-system=${this.systemNumber}`;

        this.companyId = system?.companyId;
        this.siteId = system?.siteId;
        this.systemId = system?.systemId;
        this.systemIP = system.ip || '';

        // reset machine list when system changes
        this.machines = [];

        // (moved) MQTT connection will be started after machines are loaded
      }),
      takeUntil(this.destroy$)
    ) as any;

    this.system$
      .pipe(
        filter((system: any) => !!system),
        switchMap((system: any) => this.systemService.getMachines(system.companyId, system.siteId, system.systemId)),
        takeUntil(this.destroy$)
      )
      .subscribe(machinesSnap => {
        const vmachines = machinesSnap.docs
          .map(doc => ({ ...doc.data(), machineId: doc.id }))
          .sort((a, b) => (a['name'] || '').localeCompare(b['name'] || ''));

        this.machines = vmachines.map(machineData => ({
          index: machineData['index'],
          name: machineData['name'],
          machineId: machineData['machineId'],
          communicationStatus: '',
          type: 'Unknown',
          online: false,
          ip: '',
          location: '',
          state: 'unknown',
          timestamp: 0,
          isStale: true
        }));

        // Now that machines are loaded, connect to MQTT
        this.connectToMqtt();
      });
  }
  ngOnInit(): void {
    this.auth.getUser$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(userInfo => {
        this.isTerminalAdmin = !!userInfo?.roles?.admin;
      });
  }

  @HostListener('window:focus', [])
  onFocus() {
    if (!this.mqttClient || this.mqttClient.disconnected || (this.mqttClient as any).reconnecting) {
      this.connectToMqtt();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.mqttClient) {
      // force close and remove listeners
      this.mqttClient.removeAllListeners();
      this.mqttClient.end(true);
      this.mqttClient = null;
    }
  }

  mqttLogEntries: Array<{ timestamp: string, type: string, message: string }> = [];

  mqttTimeConnected: number | null = null;

  private async connectToMqtt() {
    if (!this.systemNumber) return;

    // If already connected/reconnecting, don't create a second client.
    if (this.mqttClient && (this.mqttClient.connected || (this.mqttClient as any).reconnecting)) {
      return;
    }

    // Clean up existing client if any
    if (this.mqttClient) {
      this.mqttClient.removeAllListeners();
      this.mqttClient.end(true);
      this.mqttClient = null;
    }

    this.serverUsed = String(Math.floor(Math.random() * 2) + 1);

    let token: any = null;
    try {
      token = await this.auth.getToken();
    } catch {
      token = null;
    }

    if (!token || typeof token !== 'string') {
      this.notificationService.showNotification('error', 'Unable to connect: missing auth token.');
      return;
    }

    const client = mqtt.connect(`wss://prod${this.serverUsed}.friggkantine.no:9091/?token=${token}`,
      {
        keepalive: 30,
        clientId: 'frigg-admin-service-popup-' + this.mqttClientId
      }
    );

    this.mqttClient = client;

    client.on('connect', () => {
      this.mqttTimeConnected = Date.now() / 1000;
      this.logMqttEntries(Date.now() / 1000, 'connect', 'Connected to MQTT broker');

      client.subscribe(`systems/${this.systemNumber}/nodes/#`, (err) => {
        if (!err) {
          this.logMqttEntries(Date.now() / 1000, 'subscribe', `Subscribed to topic "systems/${this.systemNumber}/nodes"`);
        }
      });

      client.subscribe(`systems/${this.systemNumber}/status/#`, (err) => {
        if (!err) {
          this.logMqttEntries(Date.now() / 1000, 'subscribe', `Subscribed to topic "systems/${this.systemNumber}/status"`);
        }
      });
    });

    client.on('close', () => {
      // Mark unknown while disconnected
      this.systemStateUnknown = true;
    });

    client.on('error', (err: any) => {
      this.logMqttEntries(Date.now() / 1000, 'error', err?.message || String(err));
    });

    client.on('message', (topic, message) => {
      this.handleMqttMessage(topic, message);
    });
  }

  private handleMqttMessage(topic: string, message: any) {
    try {
      const msgObject = JSON.parse(message.toString());
      const timestamp = msgObject.timestamp;

      if (topic === `systems/${this.systemNumber}/status`) {
        const status = msgObject.status;
        if (status === 'online') {
          this.systemOnline = true;
          this.systemStateUnknown = false;
        } else if (status === 'offline') {
          this.systemOnline = false;
          this.systemStateUnknown = false;
        }
        this.logMqttEntries(Date.now() / 1000, 'system', status);
        return;
      }

      if (topic === `systems/${this.systemNumber}/status/version`) {
        this.version = msgObject.version;
        this.releaseDate = msgObject.releaseDate;
        return;
      }

      if (topic === `systems/${this.systemNumber}/status/jsdversion`) {
        this.jsdVersion = msgObject.status;
        return;
      }

      if (topic === `systems/${this.systemNumber}/status/modem/connection`) {
        this.network$ = of(msgObject);
        this.lastNetworkStatusUpdate = moment.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
        return;
      }

      if (topic === `systems/${this.systemNumber}/status/modem/sim`) {
        console.log("SIM INFO:", msgObject);
        this.sim$ = of(msgObject);
        return;
      }

      if (topic === `systems/${this.systemNumber}/status/modem/information`) {
        console.log("Modem INFO:", msgObject);
        this.modem$ = of(msgObject);
        return;
      }

      console.log(topic)
      // Node messages: likely under /nodes
      if (topic.includes(`/nodes/`)) {
        console.log("NODE")
        const parts = topic.split('/');
        
        const machineName = parts[3];
        const updateType = parts[5];
        if (!machineName || machineName.length === 0 || !updateType) return;

        const machine = this.machines.find(m => m.name === machineName);
        if (!machine) return;

        machine[updateType] = msgObject.status;
        console.log(machine)

        if (timestamp > (machine['timestamp'] || 0)) {
          machine['timestamp'] = timestamp;
          machine['lastestUpdate'] = moment.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
          machine['fromNow'] = moment.unix(timestamp).fromNow();
          machine['isStale'] = moment().unix() - timestamp > 600;
        }

        this.computeMachineStates(machine);

        if (updateType === 'identification') {
          this.tryParseIdentification(machine, msgObject);
        }
      }

      if (this.mqttLogEntries.length > 100) {
        this.mqttLogEntries.pop();
      }
    } catch (err) {
      console.error('Error processing MQTT message:', err);
    }
  }

  private computeMachineStates(machine: any) {
    if (machine.communicationStatus === 'Machine connected') {
      machine.state = 'ready';
      machine.statusMessage = machine.communicationStatus;
    } else {
      machine.statusMessage = machine.communicationStatus;
      machine.state = 'fault';
    }

    if (machine.machineServiceStatus !== 'Machine in service.') {
      if (machine.activeFault === 'No error') {
        machine.faultMessage = 'Unknown fault detected.';
      } else {
        machine.faultMessage = machine.activeFault;
      }
      machine.fault = true;
    } else {
      machine.faultMessage = '';
      machine.activeFault = '';
      machine.fault = false;
    }

    if (machine.door !== 'Door closed.') {
      machine.dispState = 'busy';
      machine.dispStatusMessage = 'Dispense unavailable due to open door.';
    } else if (machine.dispensAvailabilityStatus && machine.dispensAvailabilityStatus !== 'Machine free: Available to perform dispenses.') {
      machine.dispState = 'busy';
      machine.dispStatusMessage = machine.dispensAvailabilityStatus;
    } else if (machine.fault) {
      machine.dispState = 'fault';
      machine.dispStatusMessage = 'Dispense unavailable due to machine fault.';
    } else {
      machine.dispState = 'ready';
      machine.dispStatusMessage = machine.dispensAvailabilityStatus;
    }
  }

  private tryParseIdentification(machine: any, msgObject: any) {
    try {
      if (msgObject.identification) {
        machine['identification'] = msgObject.identification;
      }

      if (machine['identification'] && machine['identification'].toLowerCase().includes('vi8')) {
        machine['model'] = 'v8';
      } else if (machine['identification'] && machine['identification'].toLowerCase().includes('abcd')) {
        machine['model'] = 'SIM';
      } else {
        machine['model'] = 'v7';
      }

      const firmware = machine['identification']?.split(',')?.[3]?.split(' ')?.[0];
      if (firmware) {
        machine['model'] = machine['model'] + ' ' + firmware;
      }
    } catch (err) {
      console.error('Error parsing identification message:', err);
    }
  }

  checkIfCompatibleVersion(version: string): boolean {
    // if this.version is not defined return true but warn
    if (!this.version) {
      this.notificationService.showNotification('warning', 'Controller version is unknown. Please ensure compatibility manually.');
      return true;
    }

    // Example: Check if version is at least 16.08
    const [currentMajor, currentMinor] = this.version.split('.').map(num => parseInt(num, 10));
    const [major, minor] = version.split('.').map(num => parseInt(num, 10));
    if (currentMajor < major || (currentMajor === major && currentMinor < minor)) {
      this.notificationService.showNotification('error', `Incompatible version of controller: ${this.version}. Please update to at least ${version}.`);
      return false;
    }
    return true;
  }

  logMqttEntries(timestamp: number, type: string, message: string) {

    const formatedTimestamp = moment.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');

    this.mqttLogEntries.push({ timestamp: formatedTimestamp, type, message });
    this.mqttLogEntries = [...this.mqttLogEntries];
  }

  resetMachine(index, name) {
    console.log('Resetting machine with index:', index);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'resetMachine', payload: {
          index: index,
          name: name
        }
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }


  updateStatus() {

    if (!this.checkIfCompatibleVersion('16.10')) {
      return;
    }

    console.log('Updating status for system:', this.systemNumber);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'checkMachineStatus', payload: {}
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  requestQueUpdate() {

    if (!this.checkIfCompatibleVersion('16.10')) {
      return;
    }

    console.log('Requesting que update for system:', this.systemNumber);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'requestQueUpdate', payload: {}
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  checkAllChannelStatus() {

    if (!this.checkIfCompatibleVersion('16.31')) {
      return;
    }

    console.log('Checking all channel status for system:', this.systemNumber);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'checkAllChannelStatus', payload: {}
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  networkStatus() {

    if (!this.checkIfCompatibleVersion('16.31')) {
      return;
    }
    console.log('Checking network status for system:', this.systemNumber);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'networkStatus', payload: {}
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  startNetworkTestingLoop() {

    if (!this.checkIfCompatibleVersion('16.31')) {
      return;
    }

    console.log('Starting network testing loop for system:', this.systemNumber);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'startNetworkTestingLoop', payload: {}
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  stopNetworkTestingLoop() {

    if (!this.checkIfCompatibleVersion('16.31')) {
      return;
    }

    console.log('Stopping network testing loop for system:', this.systemNumber);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'stopNetworkTestingLoop', payload: {}
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  collectProductElevetor(index, name) {
    console.log('Collecting product elevator for machine with index:', index);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'collectProductElevetor', payload: {
          index: index,
          name: name
        }
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  resetSoldOut(index, name) {
    console.log('Resetting sold outs for machine with index:', index, name);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'resetSoldout', payload: {
          index: index,
          name: name
        }
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  setLightState(index, name, state) {
    console.log('Setting light state for machine with index:', index, name, state);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'setLightState', payload: {
          index: index,
          name: name,
          lightState: state
        }
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  readActiveFaults(index, name) {

    if (!this.checkIfCompatibleVersion('16.08')) {
      return;
    }

    console.log('Reading active faults for machine with index:', index, name);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'readActiveFaults', payload: {
          index: index,
          name: name
        }
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  readInactiveFaults(index, name) {

    if (!this.checkIfCompatibleVersion('16.08')) {
      return;
    }

    console.log('Reading inactive faults for machine with index:', index, name);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'readInactiveFaults', payload: {
          index: index,
          name: name
        }
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  requestElimination() {
    console.log('Requesting elimination for system:', this.systemNumber);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'requestElimination', payload: {

        }
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  resetJSD() {
    console.log('Resetting JSD for system:', this.systemNumber);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'resetJSD', payload: {
          systemNumber: this.systemNumber
        }
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }


  readJSDLog() {
    console.log('Reading JSD log for system:', this.systemNumber);
    if (this.mqttClient) {
      const dialogRef = this.openTimeSelectorDialog();

      dialogRef.afterClosed().subscribe(result => {
        console.log('Time selector dialog closed with result:', result);
        if (result) {
          this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
            type: 'readJSDLog', payload: {
              dateString: result
            }
          }));
        }
      });
    } else {
      console.error('MQTT client not connected');
    }
  }

  openSystemInNewWindow(siteId: string, systemId: string, machineId: string) {
    console.log('Opening system page in new window for system:', systemId, siteId, machineId);
    const url = `/dashboard/sites/${siteId}/system/${systemId}/vmmachine/${machineId}`;
    window.open(url, '_blank');
  }

  requestJSDVersion() {
    console.log('Requesting JSD version for system:', this.systemNumber);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'requestJSDVersion', payload: {
          systemNumber: this.systemNumber
        }
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  updateTemperature() {

    if (!this.checkIfCompatibleVersion('16.31')) {
      return;
    }

    console.log('Requesting temperature update for system:', this.systemNumber);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'checkTemperature', payload: {}
      }));
    } else {
      console.error('MQTT client not connected');
    }
  }

  updateMachineIdentification(machineIndex) {

    if (!this.checkIfCompatibleVersion('16.31')) {
      return;
    }

    console.log('Requesting machine identification update for machine index:', machineIndex, 'system:', this.systemNumber);
    if (this.mqttClient) {
      this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
        type: 'updateMachineIdentification', payload: {
          index: machineIndex
        }
      }));
    } else {
      console.error('MQTT client not connected');
    }

  }

  checkChannel(machineIndex, machineName) {

    if (!this.checkIfCompatibleVersion('16.31')) {
      return;
    }

    const dialogRef = this.openSelectionDialog();

    dialogRef.afterClosed().subscribe(result => {
      console.log('Channel selection dialog closed with result:', result);
      if (result) {
        this.mqttClient.publish(`systems/${this.systemNumber}/actions`, JSON.stringify({
          type: 'checkChannelStatus', payload: {
            index: machineIndex,
            name: machineName,
            selection: result
          }
        }));
      }
    });
  }

  copyToClipboard(text: any) {

    if (typeof text === 'object') {
      // Create line for each key value pair in object
      text = Object.keys(text).map(key => `${key}: ${text[key]}`).join('\n');
    }

    navigator.clipboard.writeText(text.trim()).then(() => {
      this.notificationService.showNotification('success', 'Copied to clipboard');
    }).catch(err => {
      console.error('Could not copy text: ', err);
      this.notificationService.showNotification('error', 'Failed to copy to clipboard');
    });
  }

  openTimeSelectorDialog(): MatDialogRef<TimeSelectorDialogComponent> {
    const dialogRef = this.dialog.open(TimeSelectorDialogComponent, {
      width: '400px',
    });
    return dialogRef;
  }

  openSelectionDialog(): MatDialogRef<SelectionDialogComponent> {
    const dialogRef = this.dialog.open(SelectionDialogComponent, {
      width: '300px',
    });
    return dialogRef;
  }

}
